import { cn } from '@/lib/cn'
import type { ContentStatus, CrawlRunStatus, PipelineStage, ReviewStatus } from '@/types'

/** `mixed`: a grouped row whose members' verdicts differ (Programs). */
/** A program's content status (PC-9, ADR-0018) shares `ready` with the pipeline's. */
export type BadgeStatus = ReviewStatus | PipelineStage | CrawlRunStatus | ContentStatus | 'mixed'

interface Style {
  label: string
  className: string
  dot: string
}

/**
 * Status is never colour-only (docs/01-design-system.md §7). Every badge is a
 * dot plus a text label, so the state survives greyscale and colour blindness.
 */
const STYLES: Record<BadgeStatus, Style> = {
  pending: { label: 'Pending', className: 'bg-warn-50 text-warn', dot: 'bg-warn' },
  approved: { label: 'Approved', className: 'bg-affirm-50 text-affirm', dot: 'bg-affirm' },
  rejected: { label: 'Rejected', className: 'bg-brand-red-50 text-brand-red-700', dot: 'bg-brand-red' },
  mixed: { label: 'Mixed', className: 'bg-surface-sunk text-ink-muted', dot: 'bg-ink-faint' },
  crawled: { label: 'Crawled', className: 'bg-surface-sunk text-ink-muted', dot: 'bg-ink-faint' },
  extracted: { label: 'Extracted', className: 'bg-brand-blue-50 text-brand-blue-800', dot: 'bg-brand-blue' },
  reviewed: { label: 'Reviewed', className: 'bg-brand-blue-100 text-brand-blue-800', dot: 'bg-brand-blue-800' },
  ready: { label: 'Ready', className: 'bg-affirm-50 text-affirm', dot: 'bg-affirm' },
  running: { label: 'Running', className: 'bg-brand-blue-50 text-brand-blue-800', dot: 'bg-brand-blue animate-pulse' },
  complete: { label: 'Complete', className: 'bg-affirm-50 text-affirm', dot: 'bg-affirm' },
  failed: { label: 'Failed', className: 'bg-brand-red-50 text-brand-red-700', dot: 'bg-brand-red' },
  not_started: { label: 'Not started', className: 'bg-surface-sunk text-ink-muted', dot: 'bg-ink-faint' },
  generating: { label: 'Generating', className: 'bg-brand-blue-50 text-brand-blue-800', dot: 'bg-brand-blue animate-pulse' },
  in_review: { label: 'In review', className: 'bg-warn-50 text-warn', dot: 'bg-warn' },
}

export function StatusBadge({ status, className }: { status: BadgeStatus; className?: string }) {
  const style = STYLES[status]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[4px] px-1.5 py-0.5',
        'font-head text-[11px] font-semibold',
        style.className,
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', style.dot)} aria-hidden />
      {style.label}
    </span>
  )
}

/**
 * Nav and list pending counts. Suppressed at zero — a badge reading "0" is
 * noise on every screen — and capped at 99+ so the rail never reflows.
 */
export function CountBadge({
  count,
  tone = 'warn',
  className,
}: {
  count: number
  tone?: 'warn' | 'neutral'
  className?: string
}) {
  if (count <= 0) return null
  return (
    <span
      className={cn(
        'tabular inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5',
        'py-0.5 font-head text-[11px] font-semibold',
        tone === 'warn' ? 'bg-warn-50 text-warn' : 'bg-surface-sunk text-ink-muted',
        className,
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}
